import { useEffect, useRef, useState } from 'react';
import { LIMITS, type Scene, type SceneMotion, type TransitionType } from '@gc/shared';
import { ApiError } from '../../api/client.js';
import { apiRequest } from '../../api/client.js';
import { actualizarEscena, eliminarEscena, reordenarEscenas } from '../../api/projects.js';
import { SelectorAsset } from '../biblioteca/SelectorAsset.js';

export type EditorEscenasProps = {
  readonly projectId: string;
  readonly revision: number;
  readonly scenes: readonly Scene[];
  /** Se invoca tras cada mutación: el padre recarga para obtener la revisión nueva. */
  onCambio(): void;
};

const MOVIMIENTOS: Record<SceneMotion, string> = {
  static: 'Estático',
  slow_zoom: 'Zoom lento',
  pan_horizontal: 'Barrido horizontal',
  pan_vertical: 'Barrido vertical',
};

const TRANSICIONES: Record<TransitionType, string> = {
  cut: 'Corte',
  crossfade: 'Fundido cruzado',
};

export function EditorEscenas({
  projectId,
  revision,
  scenes,
  onCambio,
}: EditorEscenasProps): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  /**
   * Centraliza el manejo de errores. Un 409 significa que la revisión quedó
   * obsoleta: se recarga para que el usuario vea el estado real en lugar de
   * reintentar a ciegas sobre datos viejos.
   */
  async function ejecutar(accion: () => Promise<unknown>): Promise<void> {
    setError(null);
    setOcupado(true);
    try {
      await accion();
      onCambio();
    } catch (causa) {
      if (causa instanceof ApiError) {
        setError(
          causa.code === 'CONFLICT'
            ? `${causa.message} Se han recargado las escenas.`
            : causa.message,
        );
        if (causa.code === 'CONFLICT') onCambio();
      } else {
        setError('La operación falló.');
      }
    } finally {
      setOcupado(false);
    }
  }

  /**
   * Regeneraciones en curso por escena. Un job suelto de regeneración no
   * forma parte de un run, así que nadie más lo vigila: aquí se consulta hasta
   * que termina y entonces se recarga el proyecto. Sin esto el usuario pulsaba
   * «Regenerar» y no veía nada hasta refrescar a mano.
   */
  const [regenerando, setRegenerando] = useState<Record<string, { jobId: string; target: 'image' | 'voice' }>>({});
  const avisar = useRef(onCambio);
  avisar.current = onCambio;
  useEffect(() => {
    const pendientes = Object.entries(regenerando);
    if (pendientes.length === 0) return;
    const intervalo = setInterval(() => {
      void (async () => {
        for (const [sceneId, { jobId }] of pendientes) {
          try {
            const job = await apiRequest<{ status: string; error: { message?: string } | null }>(`/jobs/${jobId}`);
            if (job.status === 'COMPLETED' || job.status === 'FAILED' || job.status === 'CANCELLED') {
              setRegenerando((actual) => {
                const { [sceneId]: _hecho, ...resto } = actual;
                return resto;
              });
              if (job.status === 'FAILED') setError(`La regeneración falló: ${job.error?.message ?? 'sin detalle'}`);
              avisar.current();
            }
          } catch {
            // Se reintenta en el siguiente tic.
          }
        }
      })();
    }, 3_000);
    return () => clearInterval(intervalo);
  }, [regenerando]);

  async function regenerar(sceneId: string, target: 'image' | 'voice'): Promise<void> {
    setError(null);
    try {
      const job = await apiRequest<{ id: string }>(`/projects/${projectId}/scenes/${sceneId}/regenerate`, {
        method: 'POST',
        body: { expectedRevision: revision, target },
      });
      setRegenerando((actual) => ({ ...actual, [sceneId]: { jobId: job.id, target } }));
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudo pedir la regeneración.');
      if (causa instanceof ApiError && causa.code === 'CONFLICT') onCambio();
    }
  }

  /** Mueve una escena una posición. El reorden va con la lista completa. */
  function mover(indice: number, direccion: -1 | 1): void {
    const destino = indice + direccion;
    if (destino < 0 || destino >= scenes.length) return;

    const ids = scenes.map((escena) => escena.id);
    const movida = ids[indice]!;
    ids[indice] = ids[destino]!;
    ids[destino] = movida;

    void ejecutar(() => reordenarEscenas(projectId, ids, revision));
  }

  return (
    <div>
      {error !== null && (
        <p role="alert" style={{ color: 'var(--error)' }}>
          {error}
        </p>
      )}

      <ol style={{ listStyle: 'none', padding: 0, display: 'grid', gap: '1rem' }}>
        {scenes.map((escena, indice) => (
          <li
            key={escena.id}
            style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '1rem' }}
          >
            <header style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <strong style={{ flex: 1 }}>
                {indice + 1}. {escena.title || '(sin título)'}
              </strong>
              {/* Subir y bajar son botones: accesibles con teclado sin arrastrar. */}
              <button
                type="button"
                onClick={() => mover(indice, -1)}
                disabled={ocupado || indice === 0}
                aria-label={`Subir la escena ${indice + 1}`}
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => mover(indice, 1)}
                disabled={ocupado || indice === scenes.length - 1}
                aria-label={`Bajar la escena ${indice + 1}`}
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => void regenerar(escena.id, 'image')}
                disabled={ocupado || regenerando[escena.id] !== undefined}
                aria-label={`Regenerar la imagen de la escena ${indice + 1}`}
              >
                {regenerando[escena.id]?.target === 'image' ? 'Generando imagen…' : 'Regenerar imagen'}
              </button>
              {escena.text !== null && escena.text.trim() !== '' && (
                <button
                  type="button"
                  onClick={() => void regenerar(escena.id, 'voice')}
                  disabled={ocupado || regenerando[escena.id] !== undefined}
                  aria-label={`Regenerar la narración de la escena ${indice + 1}`}
                >
                  {regenerando[escena.id]?.target === 'voice' ? 'Generando voz…' : 'Regenerar voz'}
                </button>
              )}
              {regenerando[escena.id] !== undefined && (
                <span role="status" style={{ alignSelf: 'center' }}>
                  <small>El proveedor tarda unos segundos; la escena se actualizará sola.</small>
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  void ejecutar(() => eliminarEscena(projectId, escena.id, revision));
                }}
                disabled={ocupado || scenes.length <= LIMITS.sceneMinCount}
                aria-label={`Eliminar la escena ${indice + 1}`}
              >
                Eliminar
              </button>
            </header>

            <p style={{ margin: '0.5rem 0', color: 'var(--texto-suave)' }}>{escena.description}</p>

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <label>
                Duración (s)
                <br />
                <input
                  type="number"
                  min={LIMITS.sceneDurationMinMs / 1000}
                  max={LIMITS.sceneDurationMaxMs / 1000}
                  step={0.5}
                  defaultValue={escena.durationMs / 1000}
                  disabled={ocupado}
                  // onBlur y no onChange: guardar en cada pulsación generaría
                  // una petición y un conflicto de revisión por tecla.
                  onBlur={(evento) => {
                    const segundos = Number(evento.target.value);
                    const durationMs = Math.round(segundos * 1000);
                    if (durationMs === escena.durationMs || Number.isNaN(durationMs)) return;
                    void ejecutar(() =>
                      actualizarEscena(projectId, escena.id, {
                        expectedRevision: revision,
                        durationMs,
                      }),
                    );
                  }}
                  style={{ width: '6rem' }}
                />
              </label>

              <label>
                Movimiento
                <br />
                <select
                  defaultValue={escena.motion}
                  disabled={ocupado}
                  onChange={(evento) => {
                    void ejecutar(() =>
                      actualizarEscena(projectId, escena.id, {
                        expectedRevision: revision,
                        motion: evento.target.value as SceneMotion,
                      }),
                    );
                  }}
                >
                  {Object.entries(MOVIMIENTOS).map(([valor, etiqueta]) => (
                    <option key={valor} value={valor}>
                      {etiqueta}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Transición
                <br />
                <select
                  defaultValue={escena.transition}
                  disabled={ocupado}
                  onChange={(evento) => {
                    void ejecutar(() =>
                      actualizarEscena(projectId, escena.id, {
                        expectedRevision: revision,
                        transition: evento.target.value as TransitionType,
                      }),
                    );
                  }}
                >
                  {Object.entries(TRANSICIONES).map(([valor, etiqueta]) => (
                    <option key={valor} value={valor}>
                      {etiqueta}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {escena.text !== null && (
              <>
                <h4 style={{ marginBottom: '0.25rem' }}>Narración</h4>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{escena.text}</p>
              </>
            )}

            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
              <div>
                <h4 style={{ margin: '0 0 0.25rem' }}>Visual</h4>
                {escena.visualAssetId !== null && (
                  <img
                    src={`/api/v1/assets/${escena.visualAssetId}/content`}
                    alt={`Visual de la escena ${indice + 1}`}
                    style={{
                      width: '10rem',
                      height: '5.6rem',
                      objectFit: 'cover',
                      display: 'block',
                      marginBottom: '0.25rem',
                      background: 'var(--fondo-hundido)',
                    }}
                  />
                )}
                <SelectorAsset
                  projectId={projectId}
                  usage="SCENE_VISUAL"
                  seleccionado={escena.visualAssetId}
                  deshabilitado={ocupado}
                  etiquetaSubida="Subir una imagen o clip propio"
                  onSeleccionar={(assetId) => {
                    void ejecutar(() =>
                      actualizarEscena(projectId, escena.id, {
                        expectedRevision: revision,
                        visualAssetId: assetId,
                      }),
                    );
                  }}
                />
              </div>

              <div>
                <h4 style={{ margin: '0 0 0.25rem' }}>Voz</h4>
                {escena.voiceAssetId !== null && (
                  <audio
                    src={`/api/v1/assets/${escena.voiceAssetId}/content`}
                    controls
                    preload="metadata"
                    style={{ display: 'block', marginBottom: '0.25rem', maxWidth: '16rem' }}
                  />
                )}
                <SelectorAsset
                  projectId={projectId}
                  usage="SCENE_VOICE"
                  seleccionado={escena.voiceAssetId}
                  deshabilitado={ocupado}
                  etiquetaSubida="Subir una locución propia (MP3, WAV, AAC o M4A)"
                  onSeleccionar={(assetId) => {
                    void ejecutar(() =>
                      actualizarEscena(projectId, escena.id, {
                        expectedRevision: revision,
                        voiceAssetId: assetId,
                      }),
                    );
                  }}
                />
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
